import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  reload,
  verifyPasswordResetCode,
} from 'firebase/auth';
import Input from '../components/Input';
import AccountAccessShell, {
  AccountAccessActions,
  AccountAccessErrorText,
  AccountAccessFieldStack,
  AccountAccessForm,
  AccountAccessInfoText,
  AccountAccessLabel,
  AccountAccessMeta,
  AccountAccessMetaLabel,
  AccountAccessMetaValue,
  AccountAccessPrimaryButton,
  AccountAccessSecondaryButton,
  accountAccessInputCss,
} from '../components/auth/AccountAccessShell';
import { auth } from '../lib/firebase';
import { getPasswordPolicyError, PASSWORD_POLICY_HINT } from '../lib/passwordPolicy';

type ViewState = 'loading' | 'ready' | 'success' | 'error';
type SupportedMode = 'resetPassword' | 'verifyEmail' | 'recoverEmail';

const AuthInput = styled(Input)`
  ${accountAccessInputCss};
`;

function isSupportedMode(value: string | null): value is SupportedMode {
  return value === 'resetPassword' || value === 'verifyEmail' || value === 'recoverEmail';
}

function safeContinuePath(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const parsed = new URL(raw, window.location.origin);
    if (parsed.origin !== window.location.origin) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

function friendlyActionMessage(error: any): string {
  const code = String(error?.code ?? '');
  const message = String(error?.message ?? '');
  switch (code) {
    case 'auth/expired-action-code':
      return 'This link expired. Request a new email and try again.';
    case 'auth/invalid-action-code':
      return 'This link is invalid. Request a fresh email and try again.';
    case 'auth/weak-password':
      return 'Choose a stronger password and try again.';
    case 'auth/user-disabled':
      return 'This account is disabled. Contact support if you need help.';
    default:
      return message || 'This request could not be completed.';
  }
}

export default function AuthActionPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const mode = params.get('mode');
  const oobCode = params.get('oobCode');
  const continuePath = safeContinuePath(params.get('continueUrl'));

  const [state, setState] = useState<ViewState>('loading');
  const [message, setMessage] = useState('');
  const [accountEmail, setAccountEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const passwordPolicyError = useMemo(
    () => getPasswordPolicyError(password),
    [password],
  );

  useEffect(() => {
    let active = true;
    if (!isSupportedMode(mode) || !oobCode) {
      setState('error');
      setMessage('This action link is invalid or incomplete.');
      return () => {
        active = false;
      };
    }

    setState('loading');
    setMessage('');
    setPassword('');
    setConfirmPassword('');

    (async () => {
      try {
        if (mode === 'resetPassword') {
          const email = await verifyPasswordResetCode(auth, oobCode);
          if (!active) return;
          setAccountEmail(email);
          setState('ready');
          return;
        }

        if (mode === 'verifyEmail') {
          await applyActionCode(auth, oobCode);
          if (auth.currentUser) {
            try {
              await reload(auth.currentUser);
            } catch {
              // noop
            }
          }
          if (!active) return;
          setState('success');
          setMessage('Your email address is verified. You can continue to your workspace.');
          return;
        }

        const info = await checkActionCode(auth, oobCode);
        await applyActionCode(auth, oobCode);
        if (!active) return;
        setAccountEmail(String((info.data as any)?.email ?? ''));
        setState('success');
        setMessage('Your previous email address has been restored.');
      } catch (error: any) {
        if (!active) return;
        setState('error');
        setMessage(friendlyActionMessage(error));
      }
    })();

    return () => {
      active = false;
    };
  }, [mode, oobCode]);

  const handleResetPassword = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!oobCode || mode !== 'resetPassword' || busy) return;
    if (passwordPolicyError) {
      setState('ready');
      setMessage(passwordPolicyError);
      return;
    }
    if (!confirmPassword.trim()) {
      setState('ready');
      setMessage('Please confirm your password.');
      return;
    }
    if (password !== confirmPassword) {
      setState('ready');
      setMessage('Passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setState('success');
      setMessage('Your password has been updated. Sign in with your new password to continue.');
    } catch (error: any) {
      setState('error');
      setMessage(friendlyActionMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const handleReturnToSignIn = () => {
    navigate('/', { replace: true });
  };

  const handleContinue = () => {
    if (mode === 'resetPassword') {
      navigate('/', { replace: true });
      return;
    }
    if (continuePath) {
      navigate(continuePath, { replace: true });
      return;
    }
    if (auth.currentUser) {
      navigate('/dashboard/home', { replace: true });
      return;
    }
    navigate('/', { replace: true });
  };

  let title = 'Checking your secure link';
  let copy = 'Please wait while we validate this request.';
  let body: ReactNode = null;

  if (state === 'ready' && mode === 'resetPassword') {
    title = 'Reset your password';
    copy = 'Set a new password to restore access to your CyBard account.';
    body = (
      <>
        {accountEmail ? (
          <AccountAccessMeta>
            <AccountAccessMetaLabel>Account</AccountAccessMetaLabel>
            <AccountAccessMetaValue>{accountEmail}</AccountAccessMetaValue>
          </AccountAccessMeta>
        ) : null}
        <AccountAccessForm onSubmit={handleResetPassword}>
          <AccountAccessFieldStack>
            <AccountAccessLabel htmlFor="new-password">New password</AccountAccessLabel>
            <AuthInput
              id="new-password"
              type="password"
              autoComplete="new-password"
              placeholder={PASSWORD_POLICY_HINT}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </AccountAccessFieldStack>
          <AccountAccessFieldStack>
            <AccountAccessLabel htmlFor="confirm-password">Confirm password</AccountAccessLabel>
            <AuthInput
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              placeholder="Repeat your password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
          </AccountAccessFieldStack>
          {message ? <AccountAccessInfoText>{message}</AccountAccessInfoText> : null}
          {!message && passwordPolicyError && password ? (
            <AccountAccessInfoText>{passwordPolicyError}</AccountAccessInfoText>
          ) : null}
          <AccountAccessActions>
            <AccountAccessPrimaryButton type="submit" disabled={busy}>
              {busy ? 'Saving...' : 'Save new password'}
            </AccountAccessPrimaryButton>
            <AccountAccessSecondaryButton
              type="button"
              onClick={handleReturnToSignIn}
              disabled={busy}
            >
              Return to sign in
            </AccountAccessSecondaryButton>
          </AccountAccessActions>
        </AccountAccessForm>
      </>
    );
  } else if (state === 'success') {
    title =
      mode === 'resetPassword'
        ? 'Password updated'
        : mode === 'verifyEmail'
          ? 'Email verified'
          : 'Email restored';
    copy = message;
    body = (
      <>
        {accountEmail ? (
          <AccountAccessMeta>
            <AccountAccessMetaLabel>Account</AccountAccessMetaLabel>
            <AccountAccessMetaValue>{accountEmail}</AccountAccessMetaValue>
          </AccountAccessMeta>
        ) : null}
        <AccountAccessActions>
          <AccountAccessPrimaryButton type="button" onClick={handleContinue}>
            {mode === 'resetPassword' ? 'Return to sign in' : 'Continue'}
          </AccountAccessPrimaryButton>
          {mode !== 'resetPassword' ? (
            <AccountAccessSecondaryButton type="button" onClick={handleReturnToSignIn}>
              Return to sign in
            </AccountAccessSecondaryButton>
          ) : null}
        </AccountAccessActions>
      </>
    );
  } else if (state === 'error') {
    title = 'Action link unavailable';
    copy = 'This secure request could not be completed.';
    body = (
      <>
        {message ? <AccountAccessErrorText>{message}</AccountAccessErrorText> : null}
        <AccountAccessActions>
          <AccountAccessPrimaryButton type="button" onClick={handleReturnToSignIn}>
            Return to sign in
          </AccountAccessPrimaryButton>
        </AccountAccessActions>
      </>
    );
  }

  return (
    <AccountAccessShell title={title} copy={copy}>
      {body}
    </AccountAccessShell>
  );
}
